import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { phraseService } from '../services/phraseService';
import { wordService } from '../services/wordService';
import { teamService, ROLES } from '../services/teamService';
import { TopBar } from '../components/common/TopBar';
import { Modal } from '../components/common/Modal';
import { LoginButton } from '../components/common/LoginButton';
import './PhraseManager.css';

const PAGE_SIZE = 20;

// ── Word status helpers ────────────────────────────────────
const getWordStatus = (word, statuses) => {
  const s = statuses[word?.toUpperCase()];
  if (!s) return 'none';
  if (s.phrase) return 'done'; // green
  if (s.skip) return 'skip';  // yellow
  return 'none';               // grey
};

// ── Team Selector Component ────────────────────────────────
const TeamSelector = ({ teams, onSelect }) => (
  <div className="pm-team-selector">
    <p className="pm-team-selector__intro">Seleziona una squadra per gestire il suo database di frasi:</p>
    <div className="pm-team-list">
      {teams.map((t) => {
        const roleInfo = ROLES.find((r) => r.value === t._myRole);
        return (
          <button key={t.id} className="pm-team-card" onClick={() => onSelect(t)} id={`pm-team-${t.id}`}>
            <div className="pm-team-card__avatar">{t.name.charAt(0).toUpperCase()}</div>
            <div className="pm-team-card__info">
              <span className="pm-team-card__name">{t.name}</span>
              {roleInfo && <span className="pm-team-card__role">{roleInfo.icon} {roleInfo.label}</span>}
            </div>
            <span className="pm-team-card__arrow">›</span>
          </button>
        );
      })}
    </div>
  </div>
);

// ── Phrase Manager Page ────────────────────────────────────
const PhraseManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  // Team from location.state (from Study) or from selector
  const preselectedTeamId = location.state?.teamId;
  const preselectedTeamName = location.state?.teamName;

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(
    preselectedTeamId ? { id: preselectedTeamId, name: preselectedTeamName || '' } : null
  );

  const [allWords, setAllWords] = useState([]);       // sorted list of word strings
  const [statuses, setStatuses] = useState({});       // { WORD: { phrase?, skip? } }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Active word for editing
  const [activeWord, setActiveWord] = useState(null);
  const [editing, setEditing] = useState({ phrase: '', parts: [] });
  const [modalOpen, setModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const listRef = useRef(null);

  // ── Load teams if no preselection ───────────────────────
  useEffect(() => {
    if (preselectedTeamId || !currentUser) { setLoading(false); return; }
    const load = async () => {
      try {
        const [created, joined] = await Promise.all([
          teamService.getTeamsByUser(currentUser.uid),
          teamService.getAllTeamsByUser(currentUser.uid),
        ]);
        const map = {};
        [...created, ...joined].forEach((t) => { map[t.id] = t; });
        const all = Object.values(map).map((t) => ({
          ...t, _myRole: teamService.getUserRole(t, currentUser.uid),
        }));
        setTeams(all);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [currentUser, preselectedTeamId]);

  // ── Load words + statuses when team is selected ──────────
  const loadTeamData = useCallback(async (teamId) => {
    setLoading(true);
    try {
      const [words, statusMap] = await Promise.all([
        wordService.getWords(500),
        phraseService.getTeamWordStatuses(teamId),
      ]);
      // Sort: words with status first (done/skip), then alphabetically
      const sorted = [...words].map((w) => w.toUpperCase()).sort((a, b) => a.localeCompare(b));
      setAllWords(sorted);
      setStatuses(statusMap);
    } catch (err) { console.error(err); }
    setLoading(false);
    setVisibleCount(PAGE_SIZE);
  }, []);

  useEffect(() => {
    if (selectedTeam?.id) loadTeamData(selectedTeam.id);
  }, [selectedTeam, loadTeamData]);

  // ── Infinite scroll ──────────────────────────────────────
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
        setVisibleCount((v) => v + PAGE_SIZE);
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // ── Filtered & sliced list ───────────────────────────────
  const filtered = useMemo(() => {
    let list = allWords;
    if (search.trim()) {
      list = list.filter((w) => w.includes(search.trim().toUpperCase()));
    }
    if (onlyIncomplete) {
      list = list.filter((w) => getWordStatus(w, statuses) === 'none');
    }
    return list;
  }, [allWords, search, statuses, onlyIncomplete]);

  const visible = filtered.slice(0, visibleCount);

  // ── Stats ────────────────────────────────────────────────
  const totalCompleted = useMemo(
    () => allWords.filter((w) => getWordStatus(w, statuses) !== 'none').length,
    [allWords, statuses]
  );

  // ── Active word navigation ───────────────────────────────
  const openWord = (word) => {
    const s = statuses[word] || {};
    setActiveWord(word);
    setEditing({ phrase: s.phrase || '', parts: s.parts || [] });
    setModal(true);
  };

  const navigateWord = (dir) => {
    if (!activeWord) return;
    const idx = filtered.indexOf(activeWord);
    const next = idx + dir;
    if (next >= 0 && next < filtered.length) openWord(filtered[next]);
  };

  // ── Save phrase ──────────────────────────────────────────
  const handleSave = async () => {
    if (!activeWord || !currentUser || !selectedTeam) return;
    setSaving(true);
    await phraseService.savePhrase(activeWord, editing.phrase, editing.parts, currentUser.uid, selectedTeam.id);
    setStatuses((prev) => ({
      ...prev,
      [activeWord]: { ...prev[activeWord], phrase: editing.phrase, parts: editing.parts, skip: false },
    }));
    setSaving(false);
    setModal(false);
  };

  // ── Toggle skip ──────────────────────────────────────────
  const handleSkip = async () => {
    if (!activeWord || !currentUser || !selectedTeam) return;
    const cur = statuses[activeWord];
    const isSkipped = cur?.skip === true;
    await phraseService.markWordAsSkip(selectedTeam.id, activeWord, currentUser.uid, isSkipped);
    setStatuses((prev) => ({
      ...prev,
      [activeWord]: { ...prev[activeWord], skip: !isSkipped, phrase: isSkipped ? prev[activeWord]?.phrase : undefined },
    }));
  };

  // ── Delete phrase ────────────────────────────────────────
  const handleDelete = async () => {
    if (!activeWord || !currentUser || !selectedTeam) return;
    await phraseService.deletePhrase(activeWord, currentUser.uid, selectedTeam.id);
    setStatuses((prev) => {
      const n = { ...prev };
      delete n[activeWord];
      return n;
    });
    setModal(false);
  };

  // ── Auto-split ───────────────────────────────────────────
  const handleAutoSplit = () => {
    const tokens = editing.phrase.split(/\s+/).filter(Boolean);
    setEditing((e) => ({ ...e, parts: tokens.map((text, i) => ({ speaker: (i % 2) + 1, text })) }));
  };

  const togglePartSpeaker = (i) => {
    setEditing((e) => ({ ...e, parts: e.parts.map((p, idx) => idx === i ? { ...p, speaker: p.speaker === 1 ? 2 : 1 } : p) }));
  };

  // ── Not logged in ────────────────────────────────────────
  if (!currentUser) {
    return (
      <div className="pm-page">
        <TopBar title="Gestisci Frasi" onBack={() => navigate('/study')} />
        <div className="pm-empty">
          <p>Devi essere loggato per gestire le frasi.</p>
          <LoginButton className="pm-save-btn" style={{ padding: '0.6rem 2rem' }}>Accedi</LoginButton>
        </div>
      </div>
    );
  }

  // ── Step 1: Team Selection (only if not preselected) ─────
  if (!selectedTeam) {
    return (
      <div className="pm-page">
        <TopBar title="Database Squadra" onBack={() => navigate('/study')} />
        <div className="pm-content">
          {loading ? (
            <p className="pm-loading">Caricamento squadre…</p>
          ) : teams.length === 0 ? (
            <div className="pm-empty">
              <span style={{ fontSize: '3rem' }}>🏆</span>
              <p>Devi far parte di una squadra per gestire le frasi.</p>
              <button className="pm-save-btn" onClick={() => navigate('/teams')} style={{ marginTop: '1rem' }}>
                Vai alle Squadre →
              </button>
            </div>
          ) : (
            <TeamSelector teams={teams} onSelect={setSelectedTeam} />
          )}
        </div>
      </div>
    );
  }

  // ── Step 2: Word List ────────────────────────────────────
  const activeStatus = activeWord ? getWordStatus(activeWord, statuses) : 'none';
  const activeIdx = activeWord ? filtered.indexOf(activeWord) : -1;

  return (
    <div className="pm-page">
      <TopBar
        title="Gestisci Frasi"
        onBack={() => {
          if (preselectedTeamId) navigate(-1);
          else { setSelectedTeam(null); setAllWords([]); setStatuses({}); setSearch(''); }
        }}
      />

      {/* Header */}
      <div className="pm-header">
        <div className="pm-team-badge">
          🏆 <strong>{selectedTeam.name}</strong>
        </div>
        <div className="pm-counter">
          <span className="pm-counter__done">{totalCompleted}</span>
          <span className="pm-counter__sep">/</span>
          <span className="pm-counter__total">{allWords.length}</span>
          <span className="pm-counter__label">completate</span>
        </div>
      </div>

      {/* Search + filter */}
      <div className="pm-toolbar">
        <input
          className="pm-search__input"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }}
          placeholder="Cerca parola…"
          id="input-search-word"
        />
        <button
          className={`pm-filter-btn ${onlyIncomplete ? 'is-active' : ''}`}
          onClick={() => { setOnlyIncomplete((v) => !v); setVisibleCount(PAGE_SIZE); }}
          id="btn-filter-incomplete"
          title="Mostra solo le parole non completate"
        >
          {onlyIncomplete ? '✓ ' : ''}Incomplete
        </button>
      </div>

      {/* Word list */}
      {loading ? (
        <p className="pm-loading">Caricamento database…</p>
      ) : (
        <div className="pm-list" ref={listRef}>
          {visible.map((word) => {
            const status = getWordStatus(word, statuses);
            const phraseText = statuses[word]?.phrase;
            return (
              <button
                key={word}
                className={`pm-word-row pm-word-row--${status}`}
                onClick={() => openWord(word)}
                id={`word-${word}`}
              >
                <div className="pm-word-row__status-dot" />
                <div className="pm-word-row__content">
                  <span className="pm-word-row__word">{word}</span>
                  {status === 'done' && phraseText && (
                    <span className="pm-word-row__phrase">"{phraseText}"</span>
                  )}
                  {status === 'skip' && (
                    <span className="pm-word-row__skip">↷ Da saltare</span>
                  )}
                  {status === 'none' && (
                    <span className="pm-word-row__empty">+ Aggiungi frase</span>
                  )}
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && <p className="pm-loading">Nessuna parola trovata.</p>}
          {visibleCount < filtered.length && (
            <p className="pm-load-more">Scorri per caricare altre {Math.min(PAGE_SIZE, filtered.length - visibleCount)}…</p>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModal(false)} title={activeWord}>
        {activeWord && (
          <div className="pm-modal-body">
            {/* Word navigation */}
            <div className="pm-word-nav">
              <button className="pm-word-nav__btn" onClick={() => navigateWord(-1)} disabled={activeIdx <= 0}>←</button>
              <span className="pm-word-nav__pos">{activeIdx + 1} / {filtered.length}</span>
              <button className="pm-word-nav__btn" onClick={() => navigateWord(1)} disabled={activeIdx >= filtered.length - 1}>→</button>
            </div>

            <div className={`pm-status-badge pm-status-badge--${activeStatus}`}>
              {activeStatus === 'done' ? '✓ Frase presente' : activeStatus === 'skip' ? '↷ Salta' : '○ Non completata'}
            </div>

            <label className="pm-label">Frase completa</label>
            <input
              className="pm-input"
              value={editing.phrase}
              onChange={(e) => setEditing((ev) => ({ ...ev, phrase: e.target.value }))}
              placeholder='Es. "Ciao, come stai?"'
              id="input-phrase"
            />

            <div className="pm-modal-actions-top">
              <button className="pm-split-btn" onClick={handleAutoSplit}>⚡ Suddividi automaticamente</button>
            </div>

            {editing.parts.length > 0 && (
              <>
                <p className="pm-label">Tocca per cambiare parlante:</p>
                <div className="pm-parts">
                  {editing.parts.map((p, i) => (
                    <button
                      key={i}
                      className={`pm-part pm-part--s${p.speaker}`}
                      onClick={() => togglePartSpeaker(i)}
                    >
                      <span className="pm-part__spk">S{p.speaker}</span>
                      <span className="pm-part__text">{p.text}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="pm-modal-footer">
              <div className="pm-modal-footer__left">
                {statuses[activeWord]?.phrase && (
                  <button className="pm-delete-btn" onClick={handleDelete}>Elimina</button>
                )}
                <button
                  className={`pm-skip-btn ${activeStatus === 'skip' ? 'is-active' : ''}`}
                  onClick={handleSkip}
                >
                  {activeStatus === 'skip' ? '↩ Riprendi' : '↷ Salta'}
                </button>
              </div>
              <button className="pm-save-btn" onClick={handleSave} disabled={saving} id="btn-save-phrase">
                {saving ? '...' : 'Salva'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PhraseManager;
